import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

function Pricing() {
  const { t, i18n } = useTranslation()
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly')

  const language = (i18n.language || 'en').split('-')[0]

  const premiumPricingByLanguage: Record<
    string,
    { locale: string; currency: string; monthly: number; yearly: number }
  > = {
    en: { locale: 'en-US', currency: 'USD', monthly: 4.99, yearly: 49.99 },
    ko: { locale: 'ko-KR', currency: 'KRW', monthly: 4900, yearly: 49000 },
    ja: { locale: 'ja-JP', currency: 'JPY', monthly: 500, yearly: 5000 },
    zh: { locale: 'zh-CN', currency: 'CNY', monthly: 30, yearly: 300 },
    es: { locale: 'es-ES', currency: 'EUR', monthly: 4.99, yearly: 49.99 },
    fr: { locale: 'fr-FR', currency: 'EUR', monthly: 4.99, yearly: 49.99 },
    de: { locale: 'de-DE', currency: 'EUR', monthly: 4.99, yearly: 49.99 },
  }

  const premiumPricing = premiumPricingByLanguage[language] ?? premiumPricingByLanguage.en

  const plans = [
    {
      name: t('pricing.freePlan'),
      price: { monthly: 0, yearly: 0 },
      description: t('pricing.freeDescription'),
      features: [
        { text: t('pricing.features.allBasicTools'), included: true },
        { text: t('pricing.features.toolList'), included: true },
        { text: t('pricing.features.browserProcessing'), included: true },
        { text: t('pricing.features.darkMode'), included: true },
        { text: t('pricing.features.responsive'), included: true },
        { text: t('pricing.features.historySync'), included: false },
        { text: t('pricing.features.presets'), included: false },
        { text: t('pricing.features.bulkProcessing'), included: false },
        { text: t('pricing.features.apiAccess'), included: false },
      ],
      cta: t('pricing.startFree'),
      ctaLink: '/',
      highlighted: false,
      disabled: false,
    },
    {
      name: t('pricing.premium'),
      price: { monthly: premiumPricing.monthly, yearly: premiumPricing.yearly },
      description: t('pricing.premiumDescription'),
      features: [
        { text: t('pricing.features.allFreeFeatures'), included: true },
        { text: t('pricing.features.cloudSync'), included: true },
        { text: t('pricing.features.loadPresets'), included: true },
        { text: t('pricing.features.batchMode'), included: true },
        { text: t('pricing.features.advancedJwt'), included: true },
        { text: t('pricing.features.noAds'), included: true },
        { text: t('pricing.features.prioritySupport'), included: true },
        { text: t('pricing.features.futureApi'), included: true },
        { text: t('pricing.features.earlyAccess'), included: true },
      ],
      cta: t('pricing.startPremium'),
      ctaLink: '#',
      highlighted: true,
      disabled: true,
    },
  ]

  const faqs = [
    {
      question: t('pricing.faqItems.q1'),
      answer: t('pricing.faqItems.a1')
    },
    {
      question: t('pricing.faqItems.q2'),
      answer: t('pricing.faqItems.a2')
    },
    {
      question: t('pricing.faqItems.q3'),
      answer: t('pricing.faqItems.a3')
    },
    {
      question: t('pricing.faqItems.q4'),
      answer: t('pricing.faqItems.a4')
    },
    {
      question: t('pricing.faqItems.q5'),
      answer: t('pricing.faqItems.a5')
    },
    {
      question: t('pricing.faqItems.q6'),
      answer: t('pricing.faqItems.a6')
    },
  ]

  const formatPrice = (price: number) => {
    if (price === 0) return t('pricing.free')
    return new Intl.NumberFormat(premiumPricing.locale, {
      style: 'currency',
      currency: premiumPricing.currency,
      currencyDisplay: 'symbol',
    }).format(price)
  }

  return (
    <div className="tool-container">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">{t('pricing.title')}</h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {t('pricing.subtitle')}
        </p>
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-8">
        <div className="flex items-center gap-4 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              billingPeriod === 'monthly'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            {t('pricing.monthly')}
          </button>
          <button
            onClick={() => setBillingPeriod('yearly')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              billingPeriod === 'yearly'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                : 'text-gray-600 dark:text-gray-400'
            }`}
          >
            {t('pricing.yearly')}
            <span className="ml-2 text-xs text-green-600 dark:text-green-400">{t('pricing.freeMonths')}</span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`card relative ${
              plan.highlighted
                ? 'border-2 border-primary-500 shadow-lg'
                : ''
            }`}
          >
            {plan.highlighted && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {t('pricing.popular')}
                </span>
              </div>
            )}
            
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2">{plan.name}</h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                {plan.description}
              </p>
              <div className="mb-2">
                <span className="text-4xl font-bold">
                  {formatPrice(plan.price[billingPeriod])}
                </span>
                {plan.price[billingPeriod] > 0 && (
                  <span className="text-gray-500 dark:text-gray-400">
                    {billingPeriod === 'monthly' ? t('pricing.perMonth') : t('pricing.perYear')}
                  </span>
                )}
              </div>
              {billingPeriod === 'yearly' && plan.price.yearly > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('pricing.equivalent', { price: formatPrice(plan.price.yearly / 12) })}
                </p>
              )}
            </div>

            <ul className="space-y-3 mb-8">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className={feature.included ? 'text-green-500' : 'text-gray-400'}>
                    {feature.included ? '✓' : '✗'}
                  </span>
                  <span className={feature.included ? '' : 'text-gray-400'}>
                    {feature.text}
                  </span>
                </li>
              ))}
            </ul>

            {plan.disabled ? (
              <button
                disabled
                className="block w-full text-center py-3 px-6 rounded-lg font-medium bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              >
                {plan.cta}
              </button>
            ) : (
              <Link
                to={plan.ctaLink}
                className={`block text-center py-3 px-6 rounded-lg font-medium transition-colors ${
                  plan.highlighted
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {plan.cta}
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Feature Comparison */}
      <div className="card mb-16 overflow-x-auto">
        <h2 className="text-2xl font-bold mb-6 text-center">{t('pricing.comparison')}</h2>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-3 px-4">{t('pricing.feature')}</th>
              <th className="text-center py-3 px-4">{t('pricing.freePlan')}</th>
              <th className="text-center py-3 px-4">{t('pricing.premium')}</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {[
              { feature: 'URL Encode/Decode', free: t('pricing.unlimited'), premium: t('pricing.unlimited') },
              { feature: 'Base64 Encode/Decode', free: t('pricing.unlimited'), premium: t('pricing.unlimited') },
              { feature: 'JSON Formatter', free: t('pricing.unlimited'), premium: t('pricing.unlimited') },
              { feature: 'JWT Decoder', free: t('pricing.basic'), premium: t('pricing.advanced') },
              { feature: 'QR Code', free: t('pricing.unlimited'), premium: t('pricing.unlimited') },
              { feature: 'UUID Generator', free: '100/batch', premium: t('pricing.unlimited') },
              { feature: 'Hash Generator', free: t('pricing.unlimited'), premium: t('pricing.unlimited') },
              { feature: t('pricing.features.historySync'), free: '❌', premium: '✓' },
              { feature: t('pricing.features.presets'), free: '❌', premium: '✓' },
              { feature: t('pricing.features.bulkProcessing'), free: '❌', premium: '✓' },
              { feature: t('pricing.features.apiAccess'), free: '❌', premium: '✓' },
              { feature: t('pricing.features.noAds'), free: '❌', premium: '✓' },
              { feature: t('pricing.features.prioritySupport'), free: '❌', premium: '✓' },
            ].map((row, index) => (
              <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-3 px-4">{row.feature}</td>
                <td className="text-center py-3 px-4">{row.free}</td>
                <td className="text-center py-3 px-4 text-primary-600 dark:text-primary-400">
                  {row.premium}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* FAQ */}
      <div className="max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold mb-8 text-center">{t('pricing.faq')}</h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <details
              key={index}
              className="card group"
            >
              <summary className="font-semibold cursor-pointer list-none flex justify-between items-center">
                {faq.question}
                <span className="text-gray-400 group-open:rotate-180 transition-transform">
                  ▼
                </span>
              </summary>
              <p className="mt-4 text-gray-600 dark:text-gray-400">
                {faq.answer}
              </p>
            </details>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center mt-16">
        <div className="card bg-gradient-to-r from-primary-600 to-primary-800 text-white">
          <h2 className="text-2xl font-bold mb-4">{t('pricing.ctaTitle')}</h2>
          <p className="mb-6 text-primary-100">
            {t('pricing.ctaText')}
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            {t('pricing.ctaButton')}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Pricing
